import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import {
  LayoutDashboard,
  Briefcase,
  Users,
  Target,
  Settings,
  Activity,
  LogOut,
  ChevronLeft,
  ChevronRight,
  FileText,
  Mail,
  UserCheck,
  Calendar,
  ClipboardList,
  ClipboardCheck,
  Menu,
  X,
  Zap,
  TrendingUp,
  Bell,
  Quote,
  Share2,
  Trophy,
  Database,
  Send,
  UserPlus,
  MessageSquare,
  Handshake,
  ListTodo,
  Link,
  LayoutGrid,
  ScrollText,
  HelpCircle,
  Crown,
  Upload,
  Rocket,
  Inbox,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

// ---------------------------------------------------------------------------
// Menu collapse, 6 Sep 2026 (see claude/ATS-FULL-AUDIT-37-screens-CORRECTED-2026-09-04.md,
// "Recommended order of work" item 14, and the artifact "The Menu Plan").
//
// The menu is now grouped by what a recruiter is doing, not by where the code
// lives. Nothing was deleted: every old route still answers its URL, it just
// no longer has a menu entry. Removed from the menu (and why):
//   ToDo List          -> use Tasks (232 todos that nothing reads vs. the real Tasks module)
//   Calendar           -> use Interviews (same data, different rendering)
//   Matching Resumes   -> use Matches (same endpoint)
//   Emails, Client Emails, Communications -> use Submissions (the one carrying real sends)
//   Mail threads       -> use Mailbox (/ats/inbox); Mail threads is dead
//   Companies          -> Clients (4,897 CV-scraped employers were never a client list)
//   Mailer             -> removed (70,765-address list with a broken unsubscribe)
//   Insights           -> removed (broken; Money Alerts covers it)
//   Productivity Center-> removed (overlaps My Desk and Dashboard)
//   Audit Log          -> use Activity (Audit Log rendered 2 of 13 columns)
//   Verifications (old)-> use Background Verification (the JM-ported one that is in use)
//   Activity (old admin page) -> the rebuilt Activity feed
// AI Job Search is now admin-only: it spends API credits on every run.
// ---------------------------------------------------------------------------

// Exported for the "/" command palette (lib/commandRegistry.js reads the live
// menu, so a new entry here is searchable with no other change).
export const navItems = [
  // TODAY - what am I doing right now
  { to: '/ats/ceo', icon: Crown, label: 'CEO Cockpit', minRole: 'admin', highlight: true, section: 'Today' },
  { to: '/ats', icon: LayoutDashboard, label: 'Dashboard', minRole: 'recruiter', section: 'Today', exact: true },
  { to: '/ats/my-desk', icon: ClipboardCheck, label: 'My Desk', minRole: 'recruiter', section: 'Today', highlight: true },
  { to: '/ats/manual-selections', icon: LayoutGrid, label: 'Selections Board', minRole: 'recruiter', section: 'Today', exact: true },
  { to: '/ats/manual-selections/tasks', icon: ListTodo, label: 'Tasks', minRole: 'recruiter', section: 'Today' },
  { to: '/ats/manual-selections/interviews', icon: Calendar, label: 'Interviews', minRole: 'recruiter', section: 'Today' },
  { to: '/ats/inbox', icon: Inbox, label: 'Mailbox', minRole: 'recruiter', section: 'Today' },

  // CLIENTS - the people who pay
  { to: '/ats/clients', icon: Handshake, label: 'Clients', minRole: 'recruiter', section: 'Clients' },
  { to: '/ats/requirements', icon: ClipboardList, label: 'Requirements', minRole: 'recruiter', section: 'Clients' },
  { to: '/ats/submissions', icon: Send, label: 'Submissions', minRole: 'recruiter', section: 'Clients' },
  { to: '/ats/agreements', icon: FileText, label: 'Agreements', minRole: 'recruiter', section: 'Clients' },
  { to: '/ats/proposal-mailer', icon: Mail, label: 'Proposal Mailer', minRole: 'recruiter', section: 'Clients' },
  { to: '/ats/client-activity', icon: Activity, label: 'Client activity', minRole: 'recruiter', section: 'Clients' },
  { to: '/ats/activity', icon: ScrollText, label: 'Activity log', minRole: 'admin', section: 'Growth & Admin' }, // activity log 2026-09-08

  // CANDIDATES - the people we place
  { to: '/ats/candidates', icon: Users, label: 'Candidates', minRole: 'recruiter', section: 'Candidates' },
  { to: '/ats/jobs', icon: Briefcase, label: 'Jobs', minRole: 'recruiter', section: 'Candidates' },
  { to: '/ats/matches', icon: Target, label: 'Matches', minRole: 'recruiter', section: 'Candidates' },
  { to: '/ats/hr-pipeline', icon: Share2, label: 'HR Pipeline', minRole: 'recruiter', highlight: true, section: 'Candidates' },
  { to: '/ats/manual-selections/dossiers', icon: ScrollText, label: 'Dossiers', minRole: 'recruiter', section: 'Candidates' },
  { to: '/ats/manual-selections/public-links', icon: Link, label: 'Public CV Links', minRole: 'recruiter', section: 'Candidates' },
  { to: '/ats/bgv', icon: UserCheck, label: 'Background Verification', minRole: 'recruiter', section: 'Candidates' },

  // MONEY - the fee
  { to: '/ats/deal-closer', icon: TrendingUp, label: 'Deal Closer', minRole: 'recruiter', highlight: true, section: 'Money', agencyOnly: true },
  { to: '/ats/closing', icon: Handshake, label: 'Closing Desk', minRole: 'recruiter', section: 'Money' },
  { to: '/ats/money-alerts', icon: Bell, label: 'Money Alerts', minRole: 'recruiter', section: 'Money', agencyOnly: true },
  { to: '/ats/hired-candidates', icon: Trophy, label: 'Hired', minRole: 'recruiter', highlight: true, section: 'Money' },

  // GROWTH & ADMIN - owner-only tools
  { to: '/ats/bde', icon: Rocket, label: 'BDE Engine', minRole: 'admin', highlight: true, section: 'Growth & Admin' },
  { to: '/ats/co-bde', icon: Users, label: 'Co-BDE (supply)', minRole: 'admin', section: 'Growth & Admin' }, // co-bde 2026-09-07
  { to: '/ats/data-cleanup', icon: Database, label: 'Data Cleanup', minRole: 'admin', section: 'Growth & Admin' }, // cleanup 2026-09-07
  { to: '/ats/hr-leads', icon: UserPlus, label: 'HR Leads', minRole: 'admin', section: 'Growth & Admin' },
  { to: '/ats/retargeting', icon: MessageSquare, label: 'Retargeting', minRole: 'admin', section: 'Growth & Admin' },
  { to: '/ats/ai-job-search', icon: Zap, label: 'AI Job Search', minRole: 'admin', section: 'Growth & Admin' },
  { to: '/ats/master-database', icon: Database, label: 'Master Data', minRole: 'admin', section: 'Growth & Admin' },
  { to: '/ats/jm-import', icon: Upload, label: 'JM Import', minRole: 'admin', section: 'Growth & Admin' },
];

const Sidebar = () => {
  const { user, logout, canAccess } = useAuth();
  const { hasAgencyPermission } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Marquee state
  const [marqueeModalOpen, setMarqueeModalOpen] = useState(false);
  const [marqueeQuotes, setMarqueeQuotes] = useState(() => {
    const saved = localStorage.getItem('marquee_quotes');
    return saved ? JSON.parse(saved) : ['Welcome to DCS ATS', 'Focus on what matters', 'Close deals faster'];
  });
  const [marqueeSettings, setMarqueeSettings] = useState(() => {
    const saved = localStorage.getItem('marquee_settings');
    return saved ? JSON.parse(saved) : { speed: 50, fontSize: 14, fontStyle: 'normal' };
  });
  const [editQuotes, setEditQuotes] = useState(['', '', '', '', '', '']);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // mobile-round1 (8 Sep 2026): while the phone drawer is open the page behind
  // it must not scroll, Escape must close it, and rotating/resizing up to a
  // tablet must not leave the body locked with the drawer hidden.
  // `mobileOpen` is only ever true below md, so none of this runs on desktop.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevTouchAction = body.style.touchAction;
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    const onKeyDown = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    const onResize = () => { if (window.innerWidth >= 768) setMobileOpen(false); };
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResize);
    return () => {
      body.style.overflow = prevOverflow;
      body.style.touchAction = prevTouchAction;
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
    };
  }, [mobileOpen]);

  // Format date/time for IST
  const formatDateTime = () => {
    const options = { timeZone: 'Asia/Kolkata' };
    const day = currentTime.toLocaleDateString('en-US', { ...options, weekday: 'long' });
    const date = currentTime.toLocaleDateString('en-US', { ...options, day: 'numeric', month: 'short', year: 'numeric' });
    const time = currentTime.toLocaleTimeString('en-US', { ...options, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    return { day, date, time };
  };

  const { day, date, time } = formatDateTime();

  const handleLogout = async () => {
    const success = await logout();
    if (success) navigate('/ats/login');
  };

  const openMarqueeModal = () => {
    const filled = [...marqueeQuotes];
    while (filled.length < 6) filled.push('');
    setEditQuotes(filled);
    setMarqueeModalOpen(true);
  };

  const saveMarqueeSettings = () => {
    const validQuotes = editQuotes.filter(q => q.trim());
    setMarqueeQuotes(validQuotes.length > 0 ? validQuotes : ['Welcome to DCS ATS']);
    localStorage.setItem('marquee_quotes', JSON.stringify(validQuotes));
    localStorage.setItem('marquee_settings', JSON.stringify(marqueeSettings));
    setMarqueeModalOpen(false);
  };

  // `exact` items are only active on their own path (so "Dashboard" at /ats
  // does not light up for every /ats/... page).

  // One straight list - only role/permission filtering, no workspace modes
  const filteredNavItems = navItems.filter(item => {
    if (item.agencyOnly && !hasAgencyPermission) return false;
    return canAccess(item.minRole);
  });

  // Marquee animation duration based on speed (lower speed = slower = longer duration)
  const marqueeAnimationDuration = `${Math.max(5, 30 - (marqueeSettings.speed / 5))}s`;

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="md:hidden fixed top-3 left-3 z-[60] h-[44px] w-[44px] inline-flex items-center justify-center bg-white rounded-lg shadow-md border border-slate-200"
        data-testid="mobile-menu-toggle"
        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={mobileOpen}
        aria-controls="app-sidebar"
      >
        {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
          data-testid="mobile-overlay"
        />
      )}

      <aside
        id="app-sidebar"
        className={cn(
          'flex flex-col transition-all duration-300 fixed top-0 left-0 h-screen bg-white border-r border-slate-200 shadow-xl overflow-hidden',
          'max-md:max-w-[85vw]',
          collapsed ? 'w-16' : 'w-64',
          mobileOpen ? 'translate-x-0 z-50' : '-translate-x-full md:translate-x-0 md:z-40'
        )}
        style={{ visibility: mobileOpen ? 'visible' : undefined }}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_talent-match-129/artifacts/10z830dt_Daruwallas%20Consultancy%20Services%20LLP%20Logo.png"
              alt="DCS Logo"
              className="w-8 h-8 object-contain rounded"
            />
            {!collapsed && <span className="font-semibold text-slate-900 text-lg">DCS ATS</span>}
          </div>
        </div>

        {/* Navigation - one straight list with section labels */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto scrollbar-thin">
          {filteredNavItems.map((item, index) => {
            const prevItem = filteredNavItems[index - 1];
            const sectionHeader = (!collapsed && item.section && (!prevItem || prevItem.section !== item.section)) ? (
              <p className="px-3 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400 select-none">
                {item.section}
              </p>
            ) : null;

            return (
              <div key={item.to}>
                {sectionHeader}
                <NavLink
                  to={item.to}
                  end={!!item.exact}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'sidebar-link',
                      isActive && !item.highlight && 'active',
                      isActive && item.highlight && 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white border-purple-600 hover:from-purple-600 hover:to-indigo-700',
                      !isActive && item.highlight && 'bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 text-purple-700 hover:from-violet-100 hover:to-purple-100',
                      collapsed && 'justify-center px-2'
                    )
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  <item.icon className={cn("w-5 h-5 flex-shrink-0", item.highlight && "text-current")} />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              </div>
            );
          })}
        </nav>

        {/* Bottom: Help Center, Settings (admin), date/time, logout */}
        <div className="border-t border-slate-200 p-3 max-md:pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          <NavLink
            to="/ats/help"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              cn('sidebar-link w-full mb-1', isActive && 'active', collapsed && 'justify-center px-2')
            }
            data-testid="nav-help-center"
          >
            <HelpCircle className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span>Help Center</span>}
          </NavLink>
          {canAccess('admin') && (
            <NavLink
              to="/ats/settings"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                cn('sidebar-link w-full mb-2', isActive && 'active', collapsed && 'justify-center px-2')
              }
              data-testid="nav-settings"
            >
              <Settings className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span>Settings</span>}
            </NavLink>
          )}
          {!collapsed && (
            <div className="text-center mb-3">
              <p className="text-sm font-semibold text-slate-800">{day}, {date}</p>
              <p className="text-lg font-mono font-bold text-blue-600">{time}</p>
            </div>
          )}

          <button
            onClick={handleLogout}
            className={cn('sidebar-link w-full text-red-600 hover:bg-red-50 hover:text-red-700', collapsed && 'justify-center px-2')}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span>Logout</span>}
          </button>

          <button
            onClick={() => setCollapsed(!collapsed)}
            className="sidebar-link w-full justify-center mt-2 py-1.5 max-md:!hidden"
            data-testid="collapse-sidebar-btn"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
      </aside>

      {/* Expand button when collapsed (visible only when sidebar is collapsed) */}
      {collapsed && (
        <button
          onClick={() => setCollapsed(false)}
          className="hidden md:flex fixed left-16 top-1/2 -translate-y-1/2 z-30 w-5 h-10 bg-white border border-slate-200 rounded-r-md items-center justify-center hover:bg-slate-50 shadow-sm"
          data-testid="expand-sidebar-btn"
        >
          <ChevronRight className="w-3 h-3 text-slate-500" />
        </button>
      )}

      {/* Marquee Modal */}
      <Dialog open={marqueeModalOpen} onOpenChange={setMarqueeModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Quote className="w-5 h-5 text-purple-600" />
              Customize Your Quotes
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-slate-500">Add up to 6 quotes that will scroll in the header.</p>

            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i}>
                <Label>Quote {i + 1}</Label>
                <Input
                  value={editQuotes[i] || ''}
                  onChange={(e) => {
                    const newQuotes = [...editQuotes];
                    newQuotes[i] = e.target.value;
                    setEditQuotes(newQuotes);
                  }}
                  placeholder={`Enter quote ${i + 1}...`}
                />
              </div>
            ))}

            <div className="pt-4 border-t space-y-4">
              <div>
                <Label>Scroll Speed: {marqueeSettings.speed}%</Label>
                <Slider
                  value={[marqueeSettings.speed]}
                  onValueChange={([v]) => setMarqueeSettings(prev => ({ ...prev, speed: v }))}
                  min={10}
                  max={100}
                  step={5}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Font Size: {marqueeSettings.fontSize}px</Label>
                <Slider
                  value={[marqueeSettings.fontSize]}
                  onValueChange={([v]) => setMarqueeSettings(prev => ({ ...prev, fontSize: v }))}
                  min={12}
                  max={24}
                  step={1}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Font Style</Label>
                <Select
                  value={marqueeSettings.fontStyle}
                  onValueChange={(v) => setMarqueeSettings(prev => ({ ...prev, fontStyle: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="italic">Italic</SelectItem>
                    <SelectItem value="bold">Bold</SelectItem>
                    <SelectItem value="bold-italic">Bold Italic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setMarqueeModalOpen(false)}>Cancel</Button>
              <Button onClick={saveMarqueeSettings}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Sidebar;
